import type { ClientOffer } from 'types/seller'

import { Fragment, useState } from 'react'
import styled from 'styled-components'

import { formatDate, toCurrency, formatZipCode } from 'utils'
import { useStore } from 'store'
import { energyType } from 'common'
import copy from 'copy.json'
import { DownloadIcon, InfoIcon, PencilIcon, PlusIcon } from 'icons'

import Button from 'components/Button'
import ButtonLink from 'components/ButtonLink'
import Status from 'components/Status'
import Spinner from 'components/Spinner'
import ConfirmationWarnModal from 'modals/ConfirmationWarnModal'
import UpdateProfileFileModal from 'modals/UpdateProfileFileModal'
import DeleteButton from 'components/DeleteButton'
import DetailsWrapper from 'components/layout/DetailsWrapper'
import Detail from 'components/layout/Detail'
import EditBuiltOfferDetailsModal from 'pages/seller/EditBuiltOfferDetailsModal'
import EditPlannedOfferDetailsModal from 'pages/seller/EditPlannedOfferDetailsModal'

const form_texts = copy.common.form
const texts = copy.seller.offer_details
const button = copy.common.button
const profile_file_text = copy.modal.profile_file

type Props = {
  offer: ClientOffer
  onOfferDataRefresh: () => Promise<void>
}

export default function OfferDetailsRow({ offer, onOfferDataRefresh, ...props }: Props) {
  const { api } = useStore()
  const [loading, setLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [expandDetails, setExpandDetails] = useState(false)
  const [offerDetails, setOfferDetails] = useState<ClientOffer | null>(null)
  const [deleteOfferModalOpen, setDeleteOfferModalOpen] = useState(false)
  const [editBuiltOfferDetailsModalOpen, setEditBuiltOfferDetailsModalOpen] = useState(false)
  const [editPlannedOfferDetailsModalOpen, setEditPlannedOfferDetailsModalOpen] = useState(false)
  const [updateProfileFileModalOpen, setUpdateProfileFileModalOpen] = useState(false)

  const getClientOfferDetails = async (offerId: string) => {
    setLoading(true)

    const response = await api.getClientResourceDetails('offer', offerId)
    setOfferDetails(response)
    setLoading(false)
  }

  const handleOfferDetailsClick = (offerId: string) => {
    if (!expandDetails) {
      getClientOfferDetails(offerId)
    }

    setExpandDetails(current => !current)
  }

  const handleDownloadProfileFile = (demandId: string) => {
    api.getClientProfileFiles('offer', demandId)
  }

  const handleOfferDelete = async (offerId: string) => {
    setDeleteLoading(true)
    const success = await api.deleteClientResourceDetails('offer', offerId)

    if (!success) return false

    handleOfferDataRefresh(offerId)
    setDeleteOfferModalOpen(false)
    setDeleteLoading(false)
    return true
  }

  const handleUpdateProfileFile = async (offerId: string, profileFile: File | null) => {
    const formData = new FormData()
    if (profileFile) formData.append('profileFile', profileFile)
    const success = await api.putClientProfileFile(formData, 'offer', offerId)

    if (!success) return false

    handleOfferDataRefresh(offerId)
    return true
  }

  const handleOfferDataRefresh = (offerId: string) => {
    getClientOfferDetails(offerId)
    onOfferDataRefresh()
  }

  const handleEditOfferClick = (buildingPermitNumber: string) => {
    if (buildingPermitNumber) setEditPlannedOfferDetailsModalOpen(true)
    else setEditBuiltOfferDetailsModalOpen(true)
  }

  return (
    <Fragment {...props}>
      <tr
        className={expandDetails ? 'expandedRow firstExpandedRow' : undefined}
        data-testid={offer.business_id}
      >
        <td>
          <Status statusType={offer.status} />
        </td>

        <td>
          <strong>{energyType[offer.energy_type]}</strong>
        </td>

        <td>
          <strong>{offer.osd_name || '-'}</strong>
        </td>

        <td>{offer.electric_energy_meter_number || '-'}</td>
        <td>
          {offer.installation_power.toFixed(2).replace('.00', '')} {copy.common.MW}
        </td>
        <td>
          {offer.yearly_power_production
            ? offer.yearly_power_production + ' ' + copy.common.MWh
            : '-'}
        </td>
        <td>{toCurrency(offer.energy_price)}</td>

        <td>
          <Button
            secondary={expandDetails}
            small
            style={{ minWidth: 85, marginLeft: 'auto' }}
            onClick={() => handleOfferDetailsClick(offer.business_id)}
          >
            {loading ? <Spinner size={16} /> : expandDetails ? button.roll : button.unroll}
          </Button>
        </td>
      </tr>

      {offerDetails && (
        <>
          <tr className={expandDetails ? 'expandedRow' : 'hiddenRow'}>
            <td colSpan={8}>
              <DetailsWrapper>
                <DetailsWrapper>
                  <Detail>
                    <span>{form_texts.installation_location}</span>
                    <span>{`${formatZipCode(offerDetails.location_zip_code)} ${
                      offerDetails.location_city
                    }`}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.energy_point_number}</span>
                    <span>{offerDetails.energy_point_number || '-'}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.building_permit_number}</span>
                    <span>{offerDetails.building_permit_number || '-'}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.energy_multiplier}</span>
                    <span>{`x${offerDetails.energy_multiplier}`}</span>
                  </Detail>
                </DetailsWrapper>

                <DetailsWrapper>
                  <Detail>
                    <span>{form_texts.production_start_date}</span>
                    <span>
                      {formatDate(offerDetails.production_start_date, {
                        options: {
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric'
                        }
                      })}
                    </span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.contract_length}</span>
                    <span>
                      {copy.common.from} {offerDetails.minimal_contract_length}{' '}
                      {copy.common.months_abbr} {copy.common.to}{' '}
                      {offerDetails.maximal_contract_length} {copy.common.months_abbr}
                    </span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.created_date}</span>
                    <span>{formatDate(offerDetails.created_date)}</span>
                  </Detail>
                </DetailsWrapper>
              </DetailsWrapper>

              <DetailsWrapper style={{ marginTop: 24 }}>
                {!offerDetails.building_permit_number && (
                  <Detail>
                    <h3>{texts.subheading_profile_file} </h3>
                    <Detail>
                      {!offerDetails.has_original_profile_file && (
                        <>
                          <span>{texts.original_profile_file}</span>
                          <Button
                            type="button"
                            small
                            onClick={() => setUpdateProfileFileModalOpen(true)}
                            style={{ marginRight: 'auto' }}
                          >
                            <PlusIcon />
                            {button.add}
                          </Button>
                        </>
                      )}
                    </Detail>

                    {offerDetails.has_original_profile_file && (
                      <DownloadWrapper>
                        <Detail>
                          {offerDetails.has_original_profile_file && (
                            <>
                              <span>{texts.original_profile_file}</span>
                              <ProfileFileDownloadChangeWrapper>
                                <Button
                                  secondary
                                  small
                                  disabled={!offerDetails.has_original_profile_file}
                                  onClick={() => {
                                    handleDownloadProfileFile(offerDetails.business_id)
                                  }}
                                >
                                  <DownloadIcon />
                                  {button.download}
                                </Button>
                                {offerDetails.status === 'INACTIVE' && (
                                  <ButtonLink onClick={() => setUpdateProfileFileModalOpen(true)}>
                                    {profile_file_text.change_file}
                                  </ButtonLink>
                                )}
                              </ProfileFileDownloadChangeWrapper>
                            </>
                          )}
                        </Detail>
                      </DownloadWrapper>
                    )}
                  </Detail>
                )}

                <Detail>
                  <h3>{texts.subheading_status}</h3>
                  <Statuses>
                    <Status statusType={offerDetails.status} />
                  </Statuses>
                </Detail>

                <ActionButtons>
                  <Detail style={{ marginLeft: 'auto' }}>
                    {offerDetails.status === 'INACTIVE' ? (
                      <Button
                        small
                        secondary
                        style={{ marginTop: 'auto', height: 38 }}
                        onClick={() => handleEditOfferClick(offerDetails.building_permit_number)}
                      >
                        <PencilIcon />
                        {button.edit}
                      </Button>
                    ) : (
                      <OfferInfo>
                        <InfoIcon />
                        <span>{texts.cannot_edit_offer}</span>
                      </OfferInfo>
                    )}
                  </Detail>

                  {offerDetails.status !== 'DELETED' && offerDetails.status !== 'CLOSED' && (
                    <Detail style={{ marginLeft: 'auto' }}>
                      <DeleteButton
                        onClick={() => setDeleteOfferModalOpen(true)}
                        style={{ marginTop: 'auto' }}
                      >
                        {button.delete}
                      </DeleteButton>
                    </Detail>
                  )}
                </ActionButtons>
              </DetailsWrapper>
            </td>
          </tr>

          {/* Delete offer */}
          <ConfirmationWarnModal
            title={texts.modal.delete.title}
            description={texts.modal.delete.description}
            isModalOpen={deleteOfferModalOpen}
            onModalClose={() => setDeleteOfferModalOpen(false)}
            onModalConfirmation={() => {
              handleOfferDelete(offerDetails.business_id)
            }}
            confirmButtonLabel={button.delete}
            closeButtonLabel={button.close}
            loading={deleteLoading}
          />

          {/* Edit build offer data */}
          <EditBuiltOfferDetailsModal
            offerData={offerDetails}
            isModalOpen={editBuiltOfferDetailsModalOpen}
            onModalClose={() => setEditBuiltOfferDetailsModalOpen(false)}
            refreshData={() => handleOfferDataRefresh(offerDetails.business_id)}
          />

          {/* Edit planned offer data */}
          <EditPlannedOfferDetailsModal
            offerData={offerDetails}
            isModalOpen={editPlannedOfferDetailsModalOpen}
            onModalClose={() => setEditPlannedOfferDetailsModalOpen(false)}
            refreshData={() => handleOfferDataRefresh(offerDetails.business_id)}
          />

          {/* Update profile file modal */}
          <UpdateProfileFileModal
            title={
              offerDetails.has_original_profile_file
                ? profile_file_text.confirm_change_profile_file
                : profile_file_text.confirm_add_profile_file
            }
            description={offerDetails.has_original_profile_file ? 'change_only' : ''}
            fieldErrorName="upsert_offer_for_built_installation.profile_file"
            isModalOpen={updateProfileFileModalOpen}
            onModalClose={() => setUpdateProfileFileModalOpen(false)}
            onModalConfirmation={(file: File | null) =>
              handleUpdateProfileFile(offerDetails.business_id, file)
            }
          />
        </>
      )}
    </Fragment>
  )
}

const DownloadWrapper = styled.div`
  display: flex;
  gap: 30px;
`
const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-left: auto;
`

const Statuses = styled.div`
  display: flex;
  gap: 24px;
`

const OfferInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  font: var(--400-13);
  width: 100%;
  max-width: 275px;

  svg {
    flex: none;
  }
`

const ProfileFileDownloadChangeWrapper = styled.div`
  display: flex;
  justify-content: center;
`
